import React, { ReactNode, useCallback } from 'react';
import {
  Card,
  CardContent as MuiCardContent,
  CardActions,
  Box,
  Typography,
  IconButton,
  Collapse,
  Avatar,
  Divider,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import { useCardExpansion } from '../../../store/hooks';
import { ActionCommand, ActionCommandType } from '../../../types';

export interface CardContentProps {
  id: string;
  title: string;
  content?: string | ReactNode;
  avatarIcon: React.ReactNode;
  avatarColor?: string;
  onCommand: (command: ActionCommand) => void;
  expandableContent?: React.ReactNode;
  expandable?: boolean;
  showActions?: boolean;
  customActions?: React.ReactNode;
  leftActions?: React.ReactNode;
  containerName?: string;
  isExpandableContentEditable?: boolean;
  onCardClick?: () => void;
  showExpandIcon?: boolean;
  onMouseOver?: (event: React.MouseEvent) => void;
}

const CardContent: React.FC<CardContentProps> = ({
  id,
  title,
  content,
  avatarIcon,
  avatarColor = 'primary.main',
  onCommand,
  expandableContent,
  expandable = false,
  showActions = true,
  customActions,
  leftActions,
  containerName = 'card-container',
  isExpandableContentEditable = false,
  onCardClick,
  showExpandIcon = true,
  onMouseOver,
}) => {
  // Use the card expansion hook for Redux-managed expansion state
  const { isExpanded, expandCard, collapseCard } = useCardExpansion();

  // Get whether this specific card is expanded
  const expanded = isExpanded(id);

  // Toggle expanded state with callbacks for tracking - memoized
  const toggleExpanded = useCallback(() => {
    if (expanded) {
      collapseCard(id);
      onCommand({
        type: ActionCommandType.COLLAPSE,
      });
    } else {
      expandCard(id);
      onCommand({
        type: ActionCommandType.EXPAND,
      });
    }
  }, [expanded, collapseCard, expandCard, id, onCommand]);

  const handleAccept = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation(); // Prevent card click event
      onCommand({
        type: ActionCommandType.ACCEPT,
      });
    },
    [onCommand],
  );

  const handleReject = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation(); // Prevent card click event
      onCommand({
        type: ActionCommandType.REJECT,
      });
    },
    [onCommand],
  );

  // Determine the click handler - disable clicks when expanded
  const handleCardClick =
    expandable && !onCardClick ? (expanded ? undefined : toggleExpanded) : onCardClick;

  return (
    <Card
      sx={{
        container: `${containerName} / inline-size`,
        cursor: handleCardClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': handleCardClick
          ? {
              // transform: 'translateY(-2px)',
              boxShadow: 3,
            }
          : undefined,
      }}
      onClick={() => {
        if (handleCardClick) {
          handleCardClick();
        }
      }}
      onMouseOver={onMouseOver}
    >
      <MuiCardContent
        sx={{
          pt: 2,
          pb: 0,
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        <Avatar
          sx={{
            bgcolor: avatarColor,
            mr: 2,
            [`@container ${containerName} (max-width: 350px)`]: {
              width: 32,
              height: 32,
            },
          }}
        >
          {React.cloneElement(avatarIcon as React.ReactElement, {
            sx: {
              [`@container ${containerName} (max-width: 350px)`]: {
                fontSize: '1rem',
              },
            },
          })}
        </Avatar>

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          {content}
        </Box>

        {(expandable || showExpandIcon) && (
          <Tooltip
            arrow
            title={
              isExpandableContentEditable
                ? expanded
                  ? 'Cancel'
                  : 'Edit'
                : `Show ${expanded ? 'Less' : 'More'}`
            }
            placement="left"
          >
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                if (expandable) {
                  toggleExpanded();
                } else if (onCardClick) {
                  onCardClick();
                }
              }}
              aria-expanded={expanded}
              aria-label={
                isExpandableContentEditable
                  ? expanded
                    ? 'cancel editing'
                    : 'edit'
                  : `show ${expanded ? 'less' : 'more'}`
              }
              sx={{
                flexShrink: 0,
                alignSelf: 'flex-start',
              }}
            >
              {isExpandableContentEditable ? <EditIcon /> : <MoreVertIcon />}
            </IconButton>
          </Tooltip>
        )}
      </MuiCardContent>

      {expandable && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ mt: 2, mb: 1 }} />
          <MuiCardContent sx={{ pb: '16px !important' }}>{expandableContent}</MuiCardContent>
        </Collapse>
      )}
      {showActions && (
        <CardActions
          sx={{
            pt: 0,
            justifyContent: leftActions ? 'space-between' : 'flex-end',
            px: 2,
            pb: 2,
          }}
        >
          {customActions ? (
            customActions
          ) : (
            <>
              {leftActions && <Box sx={{ display: 'flex', gap: 1 }}>{leftActions}</Box>}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip arrow title="Reject" placement="bottom">
                  <IconButton aria-label="reject" onClick={handleReject} color="error" size="small">
                    <CloseIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip arrow title="Accept" placement="bottom">
                  <IconButton
                    aria-label="accept"
                    onClick={handleAccept}
                    color="success"
                    size="small"
                  >
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default CardContent;
